import { useState } from 'react'
import { Link, useParams } from 'react-router-dom'

import { Button } from '@/components/ui/Button'
import { Card } from '@/components/ui/Card'
import { EmptyState } from '@/components/ui/EmptyState'
import { StatusPill } from '@/components/ui/StatusPill'
import {
  useStudentApplication,
  useWithdrawApplication,
} from '@/features/applications/hooks/useApplications'
import { getErrorMessage } from '@/utils/errors'
import { formatDateTime } from '@/utils/format'

const WITHDRAWABLE_STATUSES = ['applied', 'under_review', 'shortlisted']

export function ApplicationDetailPage() {
  const { id } = useParams<{ id: string }>()
  const applicationId = Number(id)
  const { data: application, isPending, isError } = useStudentApplication(applicationId)
  const withdraw = useWithdrawApplication()
  const [apiError, setApiError] = useState<unknown>(null)

  if (isPending) return <p className="text-muted my-4">Loading application…</p>
  if (isError || !application) {
    return (
      <div className="my-4">
        <EmptyState heading="Application not found" />
      </div>
    )
  }

  const canWithdraw = WITHDRAWABLE_STATUSES.includes(application.status)

  const handleWithdraw = () => {
    setApiError(null)
    withdraw.mutate(applicationId, { onError: (error) => setApiError(error) })
  }

  return (
    <>
      <Card className="mb-3 mt-4">
        <div className="d-flex justify-content-between align-items-start flex-wrap gap-3">
          <div>
            <p className="fs-18 fw-semibold mb-0">
              <Link to={`/jobs/${application.job.id}`}>{application.job.title}</Link>
            </p>
            <p className="text-muted mb-0">{application.job.organizationName}</p>
          </div>
          <div className="text-end">
            <StatusPill status={application.status} />
            <p className="fs-12 text-muted mb-0 mt-1">
              Applied {formatDateTime(application.appliedAt)}
            </p>
          </div>
        </div>

        {Boolean(apiError) && (
          <div className="alert alert-danger mt-3" role="alert">
            {getErrorMessage(apiError)}
          </div>
        )}

        {canWithdraw && (
          <div className="mt-3">
            <Button
              variant="danger"
              confirmTitle="Withdraw this application?"
              confirmText="You won't be able to re-apply to this job after withdrawing."
              loading={withdraw.isPending}
              onClick={handleWithdraw}
            >
              Withdraw Application
            </Button>
          </div>
        )}
      </Card>

      <Card title="Status History">
        {!application.statusHistory || application.statusHistory.length === 0 ? (
          <p className="text-muted mb-0">No status changes yet.</p>
        ) : (
          <ul className="list-unstyled mb-0">
            {application.statusHistory.map((entry, idx) => (
              <li key={idx} className="d-flex gap-3 pb-3">
                <div className="flex-shrink-0">
                  <span className="avatar avatar-sm avatar-rounded bg-primary-transparent" />
                </div>
                <div>
                  <p className="mb-0 fw-medium">
                    {entry.oldStatus ? (
                      <>
                        <StatusPill status={entry.oldStatus} /> →{' '}
                        <StatusPill status={entry.newStatus} />
                      </>
                    ) : (
                      <StatusPill status={entry.newStatus} />
                    )}
                  </p>
                  <p className="fs-12 text-muted mb-0">{formatDateTime(entry.changedAt)}</p>
                  {entry.notes && <p className="fs-13 mb-0 mt-1">{entry.notes}</p>}
                </div>
              </li>
            ))}
          </ul>
        )}
      </Card>
    </>
  )
}
